import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Target, Globe2, Award, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import { ABOUT_IMAGE, BRAND } from "../lib/constants";

const VALUES = [
  { icon: Target, title: "Precision over polish", desc: "We ship things that work in production. Pretty UIs are a side effect, not the goal." },
  { icon: Globe2, title: "Global from day one", desc: "Multi-region hosting, multi-language, multi-regulator — built in, not bolted on." },
  { icon: Award, title: "Senior-only delivery", desc: "No junior engineers learning on your project. Every line is shipped by someone who's done it before." },
  { icon: Sparkles, title: "Boring tech, smart bets", desc: "We pick proven stacks for infra and reserve experimentation for places it actually compounds." },
];

const TIMELINE = [
  { year: "2018", title: "Founded", desc: "Three engineers from a tier-1 prime broker leave to build the CRM they wished they had." },
  { year: "2020", title: "First 50 brokers", desc: "Global rollout across CySEC, FCA, and FSCA-licensed brokers." },
  { year: "2022", title: "Liquidity bridge launch", desc: "Plugged into 8 tier-1 LPs with sub-millisecond routing." },
  { year: "2024", title: "Prop-firm engine", desc: "Challenge / scaling / payout suite — adopted by 90+ prop firms in 18 months." },
  { year: "2026", title: "AI risk layer", desc: "Live AI-assisted exposure & toxic-flow detection across the entire stack." },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        breadcrumb={[{ label: "About" }]}
        title={<>Built by operators, <span className="text-gradient-cyan italic">for operators.</span></>}
        lede={`${BRAND.name} brings together markets specialists and senior developers who deliver the systems behind today's broker operations — no oversized agency overhead, no trainees on your account; only practitioners you'd want on payroll.`}
      />

      {/* Big quote / mission */}
      <section className="py-16 border-y border-white/10">
        <div className="max-w-5xl mx-auto px-6">
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug theme-heading"
          >
            "Trading firms don't need more sales presentations. They need a partner who knows what
            market close looks like on a volatile Friday — because they've managed it firsthand."
          </motion.blockquote>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00C2FF] mt-6">
            — {BRAND.name} founding team
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-10 h-px bg-[#00C2FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
              / How we work
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-7 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 transition-colors"
                data-testid={`about-value-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00C2FF]/10 border border-[#00C2FF]/30 flex items-center justify-center text-[#00C2FF] mb-5">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold leading-tight tracking-tighter mb-14">
            Eight years, <span className="text-gradient-cyan italic">one mission.</span>
          </h2>
          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-[#00C2FF] via-[#00C2FF]/30 to-transparent" />
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative pl-10 pb-10 last:pb-0"
                data-testid={`about-timeline-${i}`}
              >
                <div className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-[#00C2FF] glow-ring" />
                <div className="font-mono text-xs text-[#00C2FF] tracking-[0.25em] uppercase">{t.year}</div>
                <h3 className="font-display text-xl font-semibold mt-2">{t.title}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <img src={ABOUT_IMAGE} alt="" className="w-full h-64 object-cover rounded-3xl border border-white/10 mb-10" />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Want to see if we're a fit?
          </h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto">
            30 minutes is all we need to understand your desk and tell you honestly whether we can help.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="btn-cyan inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full font-semibold"
          >
            Book a discovery call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
